import React, { SetStateAction, Dispatch } from "react";
import classNames from "classnames";

import { AttachmentPlacement } from "src/types";
import { ReactComponent as DragAndDropIcon } from "src/images/dnd.svg";
import { ReactComponent as RemoveIcon } from "src/images/remove.svg";
import { TooltipPopper } from "src/app/components/TooltipPopper/TooltipPopper";
import { ReactComponent as URLIcon } from "src/images/link-light-gray.svg";
import { AttachmentMetaData } from "../Attachment";

interface UrlAttachmentProps {
  data: AttachmentMetaData;
  place: AttachmentPlacement;
  handleRemoveAttachment: ((e: React.MouseEvent) => void) | undefined;
  isHovered: boolean;
  setIsHovered: Dispatch<SetStateAction<boolean>>;
  onGalleryAttachmentClick: ((e: React.MouseEvent) => void) | undefined;
  hideDragAndDropIcon?: boolean;
}

export default function UrlAttachment({
  data,
  place,
  handleRemoveAttachment,
  isHovered,
  setIsHovered,
  onGalleryAttachmentClick,
  hideDragAndDropIcon,
}: UrlAttachmentProps) {
  const { externalUrl } = data;

  if (place === AttachmentPlacement.PublishedContent) {
    return (
      <a
        href={data.name ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div className="attachment-wrapper attachment-wrapper__preview-url attachment-wrapper--publication-content">
          <URLIcon />
        </div>
      </a>
    );
  }

  if (place === AttachmentPlacement.Dropdown) {
    return (
      <a
        href={externalUrl}
        title={externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="attachment-wrapper attachment-wrapper--dropdown"
      >
        <URLIcon />
      </a>
    );
  }

  if (place === AttachmentPlacement.ContentProposal) {
    return (
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="attachment-wrapper"
      >
        <a href={externalUrl} target="_blank" rel="noopener noreferrer">
          <div className="attachment-file">
            <div className="attachment-file__url-icon">
              <URLIcon />
            </div>
          </div>
        </a>
        <div className="attachment-file__details">
          <div className="attachment-file__download-wrapper">
            <a
              className="attachment-file__download"
              href={externalUrl}
              title={externalUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {externalUrl}
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (place === AttachmentPlacement.ContentProposalSummary) {
    return (
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="attachment-wrapper"
        onClick={onGalleryAttachmentClick}
      >
        <div className="attachment-file">
          <div>
            <URLIcon />
          </div>
          <div className="attachment-file__details">
            <div className="attachment-file__name">{externalUrl}</div>
          </div>
        </div>
      </div>
    );
  }

  if (place === AttachmentPlacement.AttachmentPreview) {
    return (
      <TooltipPopper content={externalUrl} className="TooltipedEllipsis">
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="attachment-wrapper attachment-wrapper__preview-url"
        >
          {(!isHovered || hideDragAndDropIcon) && <URLIcon />}
          {!hideDragAndDropIcon && (
            <DragAndDropIcon
              className={classNames("dnd-icon dnd-icon--gray", {
                "dnd-icon--visible": isHovered,
              })}
            />
          )}
          <RemoveIcon
            onClick={handleRemoveAttachment}
            className="remove-icon"
          />
        </div>
      </TooltipPopper>
    );
  }
  return null;
}
